import { useId } from 'react'
import type { ReactNode } from 'react'
import { cn } from './cn'

export interface FormFieldRenderProps {
  id: string
  /** wire onto the control's aria-describedby */
  describedBy?: string
  /** wire onto the control's aria-invalid */
  invalid?: true
}

export interface FormFieldProps {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  className?: string
  /** render-prop: receives the wired id / describedBy / invalid for any control */
  children: (props: FormFieldRenderProps) => ReactNode
}

/**
 * FormField — composition wrapper that wires label↔control id, hint/error ids, and
 * aria-describedby/invalid for ANY custom control. (Input/Textarea/Select already do
 * this internally; use FormField to give the same shell to a bespoke control.)
 */
export function FormField({ label, hint, error, required, className = '', children }: FormFieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined
  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-ink-secondary mb-1">
          {label}
          {required && <span aria-hidden="true" className="text-err ml-0.5">*</span>}
        </label>
      )}
      {children({ id, describedBy, invalid: error ? true : undefined })}
      {hint && !error && <p id={hintId} className="mt-1 text-xs text-ink-muted">{hint}</p>}
      {error && <p id={errorId} role="alert" className="mt-1 text-sm text-err-ink">{error}</p>}
    </div>
  )
}
